import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Boxes, FlaskConical, Droplet, TrendingUp, Truck, ShieldCheck } from "lucide-react";
import api from "@/api/client";
import ProductCard from "@/components/ProductCard";
import PriceTrendChart from "@/components/PriceTrendChart";

const CATEGORY_META = {
  polymers: {
    label: "Polymers",
    icon: Boxes,
    image:
      "https://images.pexels.com/photos/6444851/pexels-photo-6444851.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tag: "HDPE · LDPE · PP · PVC",
  },
  chemicals: {
    label: "Chemicals",
    icon: FlaskConical,
    image:
      "https://images.pexels.com/photos/15730185/pexels-photo-15730185.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tag: "Acids · Alkalis · Solvents",
  },
  cpw_oils: {
    label: "CPW Oils",
    icon: Droplet,
    image:
      "https://images.unsplash.com/photo-1558617867-659c667b6809?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    tag: "Coconut · Palm · White Oil",
  },
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [trending, setTrending] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [pRes, cRes] = await Promise.all([
          api.get("/products"),
          api.get("/categories"),
        ]);
        setProducts(pRes.data);
        setCategories(cRes.data);
        // Pick a random trending product with a trend
        const random = pRes.data[Math.floor(Math.random() * pRes.data.length)];
        if (random) {
          const trend = await api.get(`/products/${random.id}/trend`);
          setTrending({ ...random, ...trend.data });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Hero */}
      <section className="relative bg-white text-zinc-900 overflow-hidden border-b-2 border-zinc-900">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://images.pexels.com/photos/34718931/pexels-photo-34718931.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-transparent" />
        <div className="relative px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="tag text-[#FF5A00] mb-4">BULK · B2B · MSME</div>
            <h1 className="font-display text-5xl md:text-6xl xl:text-7xl font-black leading-[0.95] tracking-tight">
              Raw materials in<br />
              <span className="text-[#FF5A00]">10 minutes.</span><br />
              Not 10 days.
            </h1>
            <p className="mt-6 text-lg text-zinc-600 max-w-lg font-light">
              India's fastest procurement rail for polymers, chemicals and edible oils.
              Live stock, live prices, live tracking — all under one dashboard.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="hero-browse-button"
                onClick={() => nav("/catalog")}
                className="bg-[#FF5A00] text-white px-6 py-3.5 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm flex items-center gap-2"
              >
                BROWSE CATALOG <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => nav("/orders")}
                data-testid="hero-orders-button"
                className="border-2 border-zinc-900 bg-white text-zinc-900 px-6 py-3.5 font-mono-data tracking-widest text-sm hover:bg-zinc-900 hover:text-white transition"
              >
                MY ORDERS
              </button>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: TrendingUp, k: "₹4.2 Cr", v: "Traded today" },
              { icon: Truck, k: "18 min", v: "Avg dispatch" },
              { icon: ShieldCheck, k: "99.4%", v: "Purity SLA" },
              { icon: Boxes, k: "2,400+", v: "Active SKUs" },
            ].map((s, i) => (
              <div
                key={i}
                className="border-2 border-zinc-900 bg-white p-5 hard-shadow-sm"
                data-testid={`hero-stat-${i}`}
              >
                <s.icon className="w-5 h-5 text-[#FF5A00]" strokeWidth={1.8} />
                <div className="font-mono-data text-3xl font-bold mt-3">{s.k}</div>
                <div className="tag mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Ticker */}
        <div className="border-t-2 border-zinc-900 bg-zinc-50 py-3 overflow-hidden">
          <div className="marquee font-mono-data text-xs tracking-wider text-zinc-800">
            {[
              "HDPE ₹92.50 ▲ 0.8%",
              "LDPE ₹108.00 ▼ 0.3%",
              "PP ₹98.75 ▲ 1.2%",
              "PVC ₹86.20 ▲ 0.5%",
              "NaOH ₹62.00 ▼ 0.2%",
              "H2SO4 ₹28.50 ▲ 0.9%",
              "Na2CO3 ₹34.00 ═ 0.0%",
              "Coconut ₹165.00 ▲ 2.1%",
              "Palm ₹128.50 ▼ 1.4%",
              "White Oil ₹145.20 ▲ 0.6%",
            ]
              .concat([
                "HDPE ₹92.50 ▲ 0.8%",
                "LDPE ₹108.00 ▼ 0.3%",
                "PP ₹98.75 ▲ 1.2%",
                "PVC ₹86.20 ▲ 0.5%",
                "NaOH ₹62.00 ▼ 0.2%",
                "H2SO4 ₹28.50 ▲ 0.9%",
                "Na2CO3 ₹34.00 ═ 0.0%",
                "Coconut ₹165.00 ▲ 2.1%",
                "Palm ₹128.50 ▼ 1.4%",
                "White Oil ₹145.20 ▲ 0.6%",
              ])
              .map((t, i) => (
                <span
                  key={i}
                  className={
                    t.includes("▲")
                      ? "text-green-700"
                      : t.includes("▼")
                      ? "text-red-600"
                      : "text-zinc-500"
                  }
                >
                  {t}
                </span>
              ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 lg:px-10 py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="tag mb-2">01 / EXPLORE</div>
            <h2 className="font-display text-4xl font-black tracking-tight">
              Shop by category
            </h2>
          </div>
          <Link
            to="/catalog"
            data-testid="view-all-link"
            className="font-mono-data text-xs tracking-widest border-b-2 border-zinc-900 pb-1 hover:text-[#FF5A00] hover:border-[#FF5A00]"
          >
            VIEW ALL →
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const c = categories.find((x) => x.key === key);
            const Icon = meta.icon;
            return (
              <Link
                key={key}
                to={`/catalog?category=${key}`}
                data-testid={`category-tile-${key}`}
                className="group relative aspect-[4/3] overflow-hidden border border-zinc-900 bg-black"
              >
                <img
                  src={meta.image}
                  alt={meta.label}
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute top-4 left-4 tag text-white">{meta.tag}</div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" />
                    <span className="font-mono-data text-xs tracking-widest">
                      {c?.count ?? 0} SKUs
                    </span>
                  </div>
                  <div className="flex items-end justify-between">
                    <h3 className="font-display text-3xl font-black tracking-tight">
                      {meta.label}
                    </h3>
                    <ArrowRight className="w-6 h-6 -rotate-45 group-hover:rotate-0 transition" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Trending + Price movement */}
      {trending && (
        <section className="px-6 lg:px-10 pb-14">
          <div className="tag mb-2">02 / MARKET INTEL</div>
          <h2 className="font-display text-4xl font-black tracking-tight mb-6">
            Price movement — 30 day
          </h2>
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="bg-white border border-zinc-900 p-6 lg:col-span-2">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="tag">TRENDING SKU</div>
                  <div className="font-display font-black text-2xl mt-1 tracking-tight">
                    {trending.name}
                  </div>
                </div>
                <Link
                  to={`/product/${trending.id}`}
                  data-testid="trending-view-button"
                  className="bg-zinc-900 text-white px-4 py-2 font-mono-data text-xs tracking-widest border-2 border-zinc-900 hard-shadow-sm"
                >
                  VIEW SKU →
                </Link>
              </div>
              <PriceTrendChart data={trending.price_trend} height={280} />
            </div>
            <div className="bg-white border-2 border-zinc-900 p-6 flex flex-col hard-shadow-sm">
              <div className="tag text-[#FF5A00]">LATEST QUOTE</div>
              <div className="font-mono-data text-6xl font-bold mt-4 text-zinc-900">
                ₹{trending.price.toFixed(2)}
              </div>
              <div className="tag mt-1">per {trending.unit} · ex GST</div>
              <div className="mt-6 space-y-3 border-t-2 border-zinc-900 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">30-day low</span>
                  <span className="font-mono-data">
                    ₹{Math.min(...trending.price_trend.map((d) => d.price)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">30-day high</span>
                  <span className="font-mono-data">
                    ₹{Math.max(...trending.price_trend.map((d) => d.price)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">In stock</span>
                  <span className="font-mono-data text-green-700">
                    {trending.stock.toLocaleString()} {trending.unit}
                  </span>
                </div>
              </div>
              <Link
                to={`/product/${trending.id}`}
                data-testid="trending-buy-button"
                className="mt-auto pt-6 bg-[#FF5A00] text-white px-4 py-3 font-mono-data text-sm tracking-widest text-center border-2 border-zinc-900 hard-shadow-sm"
              >
                BUY NOW →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Featured products */}
      <section className="px-6 lg:px-10 pb-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="tag mb-2">03 / TOP MOVERS</div>
            <h2 className="font-display text-4xl font-black tracking-tight">
              Featured SKUs
            </h2>
          </div>
        </div>
        {loading ? (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-zinc-200 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      <footer className="bg-white text-zinc-900 px-6 lg:px-10 py-10 border-t-4 border-[#FF5A00]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono-data text-xs tracking-widest text-zinc-500">
            © 2026 UDYOGG · INDUSTRIAL COMMERCE PLATFORM
          </div>
          <div className="flex gap-6 font-mono-data text-xs tracking-widest text-zinc-600">
            <span>SUPPORT · +91 22 6155 0000</span>
            <span>OPS@UDYOGG.COM</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
